import { Injectable } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import
{
    IViewMetadata,
    IGridViewMetadata,
    ITreeViewMetadata,
    ICardViewMetadata,
    ICombinedViewMetadata,
    IFormMetadata,
    IStepperMetadata,
    ICustomViewMetadata,
    ITabbedViewMetadata,
    IFormMetadataCustomPostSaveAction,
    IFormMetadataCustomCancelAction,
    GridEditType,
    SaveHandlingType,
    IGridPanelViewMetadata,
    IRecalculableFormMetadata,
    IEditFormGridViewMetadata,
    ICardListGridViewMetadata,
    RepositoryDataCollection,
    IProjectionGridMetadata,
    ClassMetadata,
    IExternalUrlViewMetadata,
} from "@tylertech/tyler-api-types";
import { MetadataService } from "./metadata.service";
import { AugmentedPropertyMetadata } from "../interfaces";

export interface ProjectionGridMetadata extends IGridViewMetadata
{
    GridProperties: IProjectionGridMetadata;
}

@Injectable({ providedIn: "root" })
export class MetadataViewService
{
    constructor(private readonly $metadataService: MetadataService) { }

    createFilterString(model: any, view: IViewMetadata): string
    {
        // Filter may need to be reevaluated after recalc
        const filterToUse = view["$$OriginalConstantFilter"] === undefined
            ? view.ConstantFilter
            : view["$$OriginalConstantFilter"];
        return this.$metadataService.CreateLoadFilter(model, view.ChildColumnName, view.ParentColumnName, filterToUse);
    }

    IsCustomViewWithParentAndChildColumns(view: IViewMetadata): view is ICustomViewMetadata
    {
        return "ControlName" in view && "ParentColumnName" in view && "ChildColumnName" in view;
    }

    IsCustom(view: IViewMetadata): view is ICustomViewMetadata
    {
        return "ControlName" in view;
    }

    IsExternalUrl(view: IViewMetadata): view is IExternalUrlViewMetadata
    {
        return "ExternalUrlProperty" in view;
    }

    IsGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return "GridProperties" in view;
    }

    IsCard(view: IViewMetadata): view is ICardViewMetadata
    {
        return "Card" in view;
    }

    IsCombinedView(view: IViewMetadata): view is ICombinedViewMetadata
    {
        return "Views" in view;
    }

    FlattenFormErrors(views: IViewMetadata[]): string[]
    {
        let errors: string[] = [];
        views.forEach(v =>
        {
            if (this.IsCombinedView(v))
            {
                errors.push(...this.FlattenFormErrors(v.Views));
            }
            else if (v.ViewMetaData?.Properties)
            {
                errors.push(...this.flattenPropertiesOnly(v.ViewMetaData));
            }
        });
        return errors.filter(x => !Utilities.IsNullOrUndefinedOrEmpty(x));
    }

    private flattenPropertiesOnly(metadata: ClassMetadata): string[]
    {
        return metadata.Properties.map((x: AugmentedPropertyMetadata) =>
        {
            let ret = [];
            if (x.ClassMetadata && !this.$metadataService.IsHiddenProperty(x))
                ret = this.flattenPropertiesOnly(x.ClassMetadata);
            else
                ret = [x.$$formControlError];
            return ret;
        }).reduce((p, c) => p.concat(c), []);
    }

    AllBaseLevelViews(form: IFormMetadata): IViewMetadata[]
    {
        return this.AllBaseLevelViewsFromArray(form.Views);
    }

    AllBaseLevelViewsFromArray(views: IViewMetadata[]): IViewMetadata[]
    {
        let ret = [];
        for (let i = 0; i < views.length; i++)
        {
            const view = views[i];
            if (this.IsCombinedView(view))
            {
                let sub = this.AllBaseLevelViewsFromArray(view["Views"]);
                for (let i = 0; i < sub.length; i++)
                {
                    ret.push(sub[i]);
                }
            } else
            {
                ret.push(view);
            }
        }
        return ret;
    }

    HideCombinedViewTitle(view: ICombinedViewMetadata)
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(view.Title))
            return true;
        for (let i = 0; i < view.Views.length; i++)
        {
            if (Utilities.IsNullOrUndefinedOrEmpty(view.Views[i].Title))
                continue;
            return view.Views[i].Title === view.Title;
        }
        return false;
    }

    IsEditFormSaveDelayed(view: IViewMetadata): boolean
    {
        if (this.IsGrid(view))
        {
            return !Utilities.IsNullOrUndefined(view.EditForm) && view.EditForm.SaveHandling === SaveHandlingType.Delay;
        }
        return !this.IsCombinedView(view) || view.Views.some(v => this.IsEditFormSaveDelayed(v));
    }

    IsTabbedView(view: IViewMetadata): view is ITabbedViewMetadata
    {
        return this.IsCombinedView(view) && "GroupBy" in view;
    }

    IsDescendantOfTabbedView(view: IViewMetadata): boolean
    {
        if (this.IsTabbedView(view))
            return true;
        if (view["$$ParentView"])
            return this.IsDescendantOfTabbedView(view["$$ParentView"]);
        return false;
    }

    InitialViewOfTabbedView(view: IViewMetadata): boolean
    {
        if (this.IsTabbedView(view["$$ParentView"]))
            return view["$$ParentView"].Views.indexOf(view) === 0;
        return this.InitialViewOfTabbedView(view["$$ParentView"]);
    }

    IsOneToOneView(view: IViewMetadata): view is IViewMetadata
    {
        return !this.IsGrid(view) && !this.IsCard(view);
    }

    IsRepositoryTypeView(view: IViewMetadata): view is IViewMetadata
    {
        return (
            !this.IsCard(view) &&
            !this.IsExternalUrl(view) &&
            !this.IsCombinedView(view) &&
            (!this.IsCustom(view) || this.IsCustomViewWithParentAndChildColumns(view)) &&
            view.IsRepositoryType
        );
    }

    /**
     * Tests if the given `view` is a 'Drill' grid view; meaning a view-only grid with some way of
     * launching an editor for a single grid entry.
     *
     * @param view `IViewMetadata` to test.
     */
    IsReadOnlyGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsGrid(view) && view.EditType === GridEditType.ReadOnlyGrid;
    }

    IsGroupedGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsGrid(view) && view.EditType === GridEditType.Groupable;
    }

    /**
     * Tests if the given `view` is a 'CellEdit' grid view; meaning it is a table that supports cell-by-cell editing
     *
     * @param view `IViewMetadata` to test.
     */
    IsCellEditGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsGrid(view) && view.EditType === GridEditType.Cell;
    }

    /**
     * Tests if the given `view` is a 'MetadataPanel' grid view; meaning it uses metadata-panel-grid
     *
     * @param view `IViewMetadata` to test.
     */
    IsMetadataPanelGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsGrid(view) && view.EditType === GridEditType.MetadataPanel;
    }

    /**
     * Tests if the given `view` is a compact 'MetadataPanel' grid view
     *
     * @param view `IViewMetadata` to test.
     */
    IsCompactMetadataPanelgrid(view: IViewMetadata): view is IGridPanelViewMetadata
    {
        return this.IsMetadataPanelGrid(view) && view.Compact === true;
    }

    /**
     * Tests if the given `view` is a view-only grid view; meaning a view-only grid without an ability to launch an editor.
     * @param view `IViewMetadata` to test.
     */
    IsViewOnlyGrid(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsGrid(view) && !view.GridProperties.CanEdit && !view.GridProperties.CanAdd;
    }

    /**
 * Tests if the given `view` is a 'Drill' grid view; meaning a view-only grid with some way of
 * launching an editor for a single grid entry.
 *
 * @param view `IViewMetadata` to test.
 */
    IsEditFormGrid(view: IViewMetadata): view is IEditFormGridViewMetadata
    {
        return this.IsReadOnlyGrid(view) && "EditForm" in view;
    }

    CanCopyRecord(view: IViewMetadata, model: any): view is IGridViewMetadata
    {
        return this.IsGrid(view) && (view.GridProperties.CanCopy || this.$metadataService.EvaluateBoolean(view.GridProperties.CanCopyProperty, null, model));
    }

    IsTable(view: IViewMetadata): view is IGridViewMetadata
    {
        return this.IsReadOnlyGrid(view) || this.IsCellEditGrid(view) || this.IsViewOnlyGrid(view) || this.IsCardList(view) || this.IsGroupedGrid(view);
    }

    IsCardList(view: IViewMetadata): view is ICardListGridViewMetadata
    {
        return this.IsGrid(view) && view.EditType === GridEditType.CardList;
    }

    IsTree(view: IViewMetadata): view is ITreeViewMetadata
    {
        return "Nodes" in view || "NodesProperty" in view;
    }

    IsProjectionGrid(view: IViewMetadata): view is ProjectionGridMetadata
    {
        return this.IsGrid(view) && "RowTypeName" in view.GridProperties;
    }

    IsRecalculableForm(form: IFormMetadata): form is IRecalculableFormMetadata
    {
        return form && "RecalculableFormType" in form;
    }

    IsSearchForm(form: IFormMetadata): form is IFormMetadata
    {
        return form && "BreadCrumbUpdateable" in form;
    }

    IsStepper(form: IFormMetadata): form is IStepperMetadata
    {
        return form && "OptionalViews" in form;
    }

    IsCustomSaveForm(form: IFormMetadata): form is IFormMetadataCustomPostSaveAction
    {
        return form && "CustomPostSaveAction" in form;
    }

    IsCustomCancelForm(form: IFormMetadata): form is IFormMetadataCustomCancelAction
    {
        return form && "CancelAction" in form;
    }

    GetTypeName(view: IViewMetadata): string
    {
        if (this.IsCombinedView(view)) return this.GetTypeName(view.Views[0]);
        else if (this.IsGrid(view)) return view.GridProperties.ClassMetadata.TypeName;
        else if (!Utilities.IsNullOrUndefined(view.ViewMetaData)) return view.ViewMetaData.TypeName;
    }

    GetGridTypeName(view: IViewMetadata): string
    {
        if (!this.IsGrid(view))
        {
            return undefined;
        }

        if (this.IsProjectionGrid(view))
        {
            return view.GridProperties.RowTypeName;
        }
        return view.GridProperties.ClassMetadata.TypeName;
    }

    GetFilterFromView(model: any, view: IViewMetadata): string
    {
        if (!view) return null;
        if (this.IsCombinedView(view) && Utilities.IsNullOrUndefinedOrEmpty(view.ConstantFilter))
        {
            return this.GetFilterFromView(model, view.Views[0]);
        }
        else
            return this.$metadataService.CreateLoadFilter(
                model,
                view.ChildColumnName,
                view.ParentColumnName,
                view.ConstantFilter
            );
    }

    IsViewCellEditing(view: IViewMetadata): boolean
    {
        return (
            (view["$$cellEditing"] ?? false) ||
            (this.IsCombinedView(view) && view.Views.some(v => this.IsViewCellEditing(v)))
        );
    }

    UpdateViewMetadata(views: IViewMetadata[], newViews: IViewMetadata[]): void
    {
        for (let i = 0, l = views.length; i < l; i++)
        {
            const newView = newViews[i];
            const view = views[i];
            if (this.IsGrid(view) && this.IsGrid(newView))
            {
                view.GridProperties.ClassMetadata = newView.GridProperties.ClassMetadata;
                view.GridProperties.Actions = newView.GridProperties.Actions;
                view.GridProperties = newView.GridProperties;
            }
            else
                view.ViewMetaData = newView.ViewMetaData;
            //should we just replace all properties at some point?
            view.SubPropertyName = newView.SubPropertyName;
            view.SubPropertyType = newView.SubPropertyType;
            view.HiddenProperty = newView.HiddenProperty;
            view.OnNext = newView.OnNext;
            view.OnPrevious = newView.OnPrevious;
            view.OnShown = newView.OnShown;
            if (this.IsCombinedView(view))
            {
                if (!this.IsCombinedView(newView))
                    delete view.Views;
                else
                    this.UpdateViewMetadata(view.Views, newViews[i]["Views"]);
            }
            else if (!this.IsCombinedView(view) && this.IsCombinedView(newView))
                view["Views"] = newView.Views;
        }
    }

    IsHidden(view: IViewMetadata, model: any, dataController: RepositoryDataCollection): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(view.HiddenProperty))
            return false;

        if (view.SubPropertyType && dataController.OtherEntities[view.SubPropertyType])
            model = dataController.OtherEntities[view.SubPropertyType][0];

        let parent = model["$$Parent"];
        if (Utilities.IsNullOrUndefined(parent))
            parent = model;

        return this.$metadataService.EvaluateBoolean(view.HiddenProperty, parent, model);
    }

    IsDisabled(view: IViewMetadata, model: any, dataController: RepositoryDataCollection): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(view?.DisabledProperty))
            return false;
        if (!model)
            return false;
        if (view.SubPropertyType && dataController.OtherEntities[view.SubPropertyType])
            model = dataController.OtherEntities[view.SubPropertyType][0];

        let parent = model["$$Parent"];
        if (Utilities.IsNullOrUndefined(parent))
            parent = model;

        return this.$metadataService.EvaluateBoolean(view.DisabledProperty, parent, model);
    }

    GetTitle(view: IViewMetadata, model: any, dataController: RepositoryDataCollection): string
    {
        if (Utilities.IsNullOrUndefined(view.TitleProperty))
            return view.Title;

        if (view.SubPropertyType && dataController.OtherEntities[view.SubPropertyType])
            model = dataController.OtherEntities[view.SubPropertyType][0];

        return this.$metadataService.DependentModuleValue(
            view.TitleProperty,
            model["$$Parent"],
            model
        );
    }

    focusFirstMetadataInput(selectedView): void
    {
        let inputSelector = "";
        let view = this.IsCombinedView(selectedView) ? (selectedView as ICombinedViewMetadata).Views[0] : selectedView;

        if(this.IsCard(view))
        {
            if(this.IsGrid(view)) //Card lists are also "grids"
            {
                inputSelector = !Utilities.IsNullOrUndefinedOrEmpty((view as ICardListGridViewMetadata)?.GroupByProperties)
                    ? " card-list forge-list card-list-group button"
                    : " card-list forge-expansion-panel card-list-item-header forge-toolbar";
            }
            else
            {
                inputSelector = "metadata-action-menu .metadata-action--button";
            }

            this.selectInput(inputSelector, false);
        }
        else if (this.IsTree(view))
        {
            inputSelector = "tree-view forge-icon-button";
            this.selectInput(inputSelector, false);
        }
        else if (this.IsGrid(view))
        {
            const gridIcon = document.querySelector("forge-icon-button.grid-icon-button") as HTMLElement;
            if (!Utilities.IsNullOrUndefined(gridIcon))
            {
                requestAnimationFrame(() => gridIcon.focus());
            }
        }
        else
        {
            // const properties: PropertyMetadata[] = view.ViewMetaData?.Properties;

            if (!Utilities.IsNullOrUndefinedOrEmpty(view.ViewMetaData?.Properties))
            {
                const input = view.ViewMetaData.Properties
                    .filter(x => ["Text", "Password", "Select", "MultiSelect", "DualList", "TextArea", "RichText", "Date", "Check", "Number", "Color", "Time", "DateTime", "PhoneNumber", "PhoneNumberWithExtension", "NumberWithoutDisplayFiltering", "Currency", "Percent", "TypeAhead", "FilePicker", "Button", "Link", "Photo", "Calendar", "MonthCalendar"].findIndex(y => y === x.InputType) > -1
                        && !Utilities.IsNullOrUndefined(x.RenderPosition))
                    .sort((a, b) => a.RenderPosition - b.RenderPosition);

                if (!Utilities.IsNullOrUndefinedOrEmpty(input))
                {
                    //TODO Handle forge-icon-button?
                    switch (input[0].InputType)
                    {
                        case "Button":
                            inputSelector = "forge-button";
                            break;

                        case "Check":
                            inputSelector = "[id^='chk_']";
                            break;

                        case "Color":
                            inputSelector = "[id^='clr_']";
                            break;

                        case "Date":
                        case "DateTime":
                            inputSelector = "[id^='txt_date_']";
                            break;

                        case "MultiSelect":
                            inputSelector = "[id^='mlt_']";
                            break;

                        case "DualList":
                            inputSelector = "[id^='searchFilter-']";
                            break;

                        case "Select":
                        case "TypeAhead":
                            inputSelector = "input";
                            break;

                        case "Calendar":
                        case "MonthCalendar":
                            inputSelector = "div[role=button]";
                            break;

                        default:
                            inputSelector = "[id^='txt_']";
                            break;
                    }

                    this.selectInput(inputSelector, true);
                }
            }
        }
    }

    private selectInput(inputSelector: string, isInputField?): void
    {
        const rootSelector = `.metadata-form--card .content-form metadata-view ${isInputField ? " .metadata-panel-row metadata-input" : ""}`;
        const element = document.querySelector(`${rootSelector} ${inputSelector}:not([disabled])`);

        if (!Utilities.IsNullOrUndefined(element))
        {
            requestAnimationFrame(() => (element as HTMLElement).focus());
        }
    }
}
